import * as React from "react";
import {inject, observer} from "mobx-react";
import type {TransportLayer} from "../data/TransportLayer";
import type {Navigation} from "../Navigation";
import type {AppState, UIVersion} from "../data/state/AppState";
import type {UpdateUserProfileRequest} from "../generated/api/base";
import {XHRLoader} from "../utils/loader/XHRLoader";
import {DateUtils} from "../utils/DateUtils";
import {NavigationEnum, SettingsSubNav} from "../Enums";
import type {IDropdownElementProps} from "./topbar/TopBarDropdown";
import {TopBarDropdown} from "./topbar/TopBarDropdown";
import {ToggleSwitchV5Styled} from "./5.0/user/UserProfileV5";

interface IUserProfileProps {
	readonly transport?: TransportLayer;
	readonly navigation?: Navigation;
	readonly appState?: AppState;
}

@inject("navigation")
@inject("transport")
@inject("appState")
@observer
export class UserProfile extends React.Component<IUserProfileProps> {
	private _avatar = React.createRef<HTMLDivElement>();

	private onSettingsClick = () => {
		this.props.navigation.goApp({
			mainNav: NavigationEnum.SETTINGS,
			subNavs: [SettingsSubNav.PERSONAL],
		});
	};

	private onMyProfileClick = () => {
		this.props.navigation.goApp({
			mainNav: NavigationEnum.SETTINGS,
			subNavs: [SettingsSubNav.PERSONAL],
		});
	};

	private onLogoutClick = () => {
		return this.props.transport.services.auth.logout();
	};

	private onUITogglerClick = () => {
		const {appState} = this.props;
		const {user, theme} = appState;

		if (appState.selectedMenu === NavigationEnum.PORTFOLIOS) {
			// In V4, we have the portfolioRefId in the URL when we're on the "Portfolios" menu
			// In V5, we shouldn't have that
			appState.app.navigation.goApp({
				mainNav: NavigationEnum.PORTFOLIOS,
			});
		}

		const newVersion: UIVersion = "5.0";
		const date = DateUtils.stringify(new Date());
		const v5ToggleCount = (user.preferences.generalSetting.v5ToggleCount ?? 0) + 1;

		appState.setUIVersion(newVersion);
		user.generalSetting.v5ToggleCount = v5ToggleCount;

		const params: UpdateUserProfileRequest = {
			firstName: user.firstName,
			lastName: user.lastName,
			username: user.username,
			phoneNumber: user.phone,
			countryCode: user.countryCode,
			userGeneralSetting: {
				timeZone: "GMT+2",
				theme: theme,
				language: "en",
				currentUiVersion: newVersion,
				lastToggleOnAt: date,
				v5ToggleCount: v5ToggleCount,
			},
		};

		return appState.app.transport.requestForOrganization<UpdateUserProfileRequest[]>({
			url: "users/updateuserprofile",
			method: XHRLoader.METHOD_POST,
			params: params,
		});
	};

	public override render() {
		const {appState} = this.props;
		const user = appState.user;
		const initials = user ? appState.actions.renderInitials(user.id) : "";

		const dropdownElements: IDropdownElementProps[] = [
			{
				icon: "user",
				onClick: this.onMyProfileClick,
				label: "My profile",
			},
			{
				icon: "settings",
				onClick: this.onSettingsClick,
				label: "Settings",
			},
			{
				icon: "v5-icon",
				onClick: this.onUITogglerClick,
				label: "Version 5 (Beta)",
				hasLineOnTop: true,
				rightContent: (
					<ToggleSwitchV5Styled
						noLabel
						value={false}
						onChange={null}
					/>
				),
			},
			{
				icon: "logout",
				onClick: this.onLogoutClick,
				label: "Logout",
			},
		];

		return (
			<div
				className="avatar avatarColor dropdown"
				ref={this._avatar}
			>
				<span className="initials">{initials}</span>
				<TopBarDropdown
					elements={dropdownElements}
					parent={this._avatar}
				/>
			</div>
		);
	}
}
